/*
 * CAPTURE-UI COMPONENT
 * TemplateStudioAssistantPanel: an INTERACTIVE propose → preview → apply loop.
 *
 * A builder chats with the write-free advisory endpoint (multi-turn: the full
 * transcript is sent each turn), and drives the human-gated apply ops per
 * proposal:
 *   - mark_field_not_applicable → apply (required=false)
 *   - configure_transform       → shows the server-computed before/after preview,
 *                                 then apply (replace transform pipeline)
 *   - set_anchor                → Preview (non-persisting re-extract: before/after)
 *                                 then apply (insert the anchor zone)
 *
 * A free-text box is the primary path — it's what lets a builder say
 * "payment_terms is grabbing the wrong line", which surfaces a set_anchor
 * proposal (wrong-location fields are success=true and are not flagged
 * unprompted). A canned "Review this test run" quick-action seeds the first
 * message, preserving the old one-shot behavior.
 *
 * Apply/preview failures are 400/404/409/422 — surfaced inline; they NEVER sign
 * out (the interceptor only signs out on a 401 invalid_token/unauthorized).
 */
import React, { useState, useRef, useEffect } from 'react';
import { captureAPI } from '../services/api';
import './TemplateStudioAssistantPanel.css';

// Canned first-turn prompt for the quick-action (the old one-shot behavior).
const REVIEW_MESSAGE =
  'Review the latest test run: which expected fields were missed vs legitimately ' +
  'not present, and are any values the wrong type?';

// Presentation + applyability per proposal kind. mark_field_not_applicable,
// configure_transform and set_anchor have apply ops; the rest are advisory-only.
const PROPOSAL_META = {
  mark_field_not_applicable: { title: 'Not applicable', applyable: true },
  configure_transform: { title: 'Transform', applyable: true },
  set_anchor: { title: 'Add anchor', applyable: true },
  suggest_field_zone: { title: 'Suggested zone', applyable: false },
  flag_low_confidence: { title: 'Low confidence', applyable: false },
};

function errorText(err) {
  return (
    err?.response?.data?.error ||
    err?.message ||
    'Something went wrong. Please try again.'
  );
}

// A 409 anchor_exists is a guard, not a failure — render it gently.
function isAnchorExists(err) {
  return err?.response?.status === 409 || /anchor_exists/i.test(errorText(err));
}

// Friendly text for a read-only preview that returned ok:false.
function previewReasonText(reason) {
  switch (reason) {
    case 'anchor_not_found':
      return 'That anchor text was not found on the source document.';
    case 'no_field_zone':
      return 'This field has no zone to re-anchor.';
    case 'field_not_found':
      return 'Field not found on this template.';
    case 'no_source_ocr':
      return 'The source document has no OCR to resolve against.';
    default:
      return `No preview available (${reason}).`;
  }
}

// The applied-state summary line, per op.
function appliedText(p, st) {
  if (p.type === 'set_anchor') {
    return `Anchor added: ${p.anchor?.anchor_text ?? ''}`;
  }
  if (p.type === 'configure_transform') {
    const after = p.preview?.after;
    return after != null && after !== '' ? `New value: "${after}"` : 'Transform applied';
  }
  // mark_field_not_applicable (and any field-state op): required diff.
  return `required: ${String(st.apply?.before?.required)} → ${String(st.apply?.after?.required)}`;
}

const TemplateStudioAssistantPanel = ({ templateId, documentId, fieldLabels = {}, onFieldApplied }) => {
  // Display transcript: { role:'user', content } | { role:'assistant', text, proposals }
  const [turns, setTurns] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // Per-proposal op state, keyed by `t<turnIndex>-p<proposalIndex>`.
  const [opState, setOpState] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    // Keep the newest turn in view as the conversation grows.
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [turns, opState]);

  const setOp = (key, patch) =>
    setOpState((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const send = async (contentOverride) => {
    const content = (contentOverride ?? input).trim();
    if (!content || sending) return;

    // Build the API transcript from prior turns (assistant turns send their text).
    const apiMessages = turns.map((t) =>
      t.role === 'user'
        ? { role: 'user', content: t.content }
        : { role: 'assistant', content: t.text || '' },
    );
    apiMessages.push({ role: 'user', content });

    setSendError(null);
    setTurns((prev) => [...prev, { role: 'user', content }]);
    setInput('');
    setSending(true);
    try {
      const data = await captureAPI.assistantReview({ templateId, documentId, messages: apiMessages });
      if (!data || data.ok === false) {
        throw new Error(data?.error || 'The assistant could not complete the review.');
      }
      setTurns((prev) => [
        ...prev,
        { role: 'assistant', text: data.text || '', proposals: Array.isArray(data.proposals) ? data.proposals : [] },
      ]);
    } catch (err) {
      setSendError(errorText(err));
    } finally {
      setSending(false);
    }
  };

  const onInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const previewAnchor = async (key, p) => {
    setOp(key, { preview: { status: 'loading' } });
    try {
      const data = await captureAPI.assistantPreviewAnchor({
        templateId,
        documentId,
        field_key: p.field_key,
        anchor_text: p.anchor?.anchor_text,
        anchor_match_mode: p.anchor?.anchor_match_mode,
      });
      if (data && data.ok) {
        setOp(key, { preview: { status: 'done', before: data.before, after: data.after } });
      } else {
        setOp(key, { preview: { status: 'done', reason: data?.reason || 'no_preview' } });
      }
    } catch (err) {
      setOp(key, { preview: { status: 'error', error: errorText(err) } });
    }
  };

  const buildApplyProposal = (p) => {
    if (p.type === 'configure_transform') {
      return { type: 'configure_transform', field_key: p.field_key, transforms: p.config?.transforms };
    }
    if (p.type === 'set_anchor') {
      return {
        type: 'set_anchor',
        field_key: p.field_key,
        anchor_text: p.anchor?.anchor_text,
        anchor_match_mode: p.anchor?.anchor_match_mode,
      };
    }
    return { type: p.type, field_key: p.field_key };
  };

  const applyProposal = async (key, p) => {
    setOp(key, { apply: { status: 'applying' } });
    try {
      const data = await captureAPI.assistantApply({ templateId, proposal: buildApplyProposal(p) });
      if (!data || data.ok === false) {
        throw new Error(data?.error || 'The change could not be applied.');
      }
      setOp(key, { apply: { status: 'applied', before: data.before, after: data.after } });
      // Op-aware callback: the parent only reflects required→false for
      // not-applicable; pass the op so it never clobbers `required` on others.
      if (onFieldApplied) onFieldApplied(p.field_key, data.after, p.type);
    } catch (err) {
      // 409 anchor_exists is a guard, not a failure — show a calm inline note.
      const soft = isAnchorExists(err);
      setOp(key, {
        apply: { status: 'error', soft, error: soft ? 'This field already has an anchor.' : errorText(err) },
      });
    }
  };

  const hasStarted = turns.length > 0 || sending;

  return (
    <div className="assistant-panel">
      <div className="assistant-header">
        <h4>Assistant</h4>
      </div>

      {!documentId && (
        <p className="assistant-hint">Add a sample document to preview and apply spatial fixes.</p>
      )}

      {/* Transcript */}
      {hasStarted && (
        <div className="assistant-transcript" ref={scrollRef}>
          {turns.map((t, ti) =>
            t.role === 'user' ? (
              <div className="assistant-turn assistant-turn-user" key={`t${ti}`}>
                <div className="assistant-bubble-user">{t.content}</div>
              </div>
            ) : (
              <div className="assistant-turn assistant-turn-assistant" key={`t${ti}`}>
                {t.text && <div className="assistant-summary">{t.text}</div>}
                {Array.isArray(t.proposals) && t.proposals.length > 0 && (
                  <div className="assistant-proposals">
                    {t.proposals.map((p, pi) => {
                      const key = `t${ti}-p${pi}`;
                      const meta = PROPOSAL_META[p.type] || { title: p.type, applyable: false };
                      const label = fieldLabels[p.field_key] || p.field_key;
                      const st = opState[key] || {};
                      const applied = st.apply?.status === 'applied';
                      return (
                        <div
                          className={`assistant-proposal proposal-${meta.applyable ? 'apply' : 'info'}`}
                          key={key}
                        >
                          <div className="proposal-row">
                            <span className="proposal-kind">{meta.title}</span>
                            <span className="proposal-label">{label}</span>
                          </div>
                          {p.rationale && <div className="proposal-rationale">{p.rationale}</div>}

                          {/* configure_transform: server-computed before/after preview */}
                          {p.type === 'configure_transform' && p.preview && (
                            <div className="proposal-preview">
                              <span className="proposal-preview-before">{String(p.preview.before ?? '—')}</span>
                              <span className="proposal-preview-arrow">→</span>
                              <span className="proposal-preview-after">{String(p.preview.after ?? '—')}</span>
                            </div>
                          )}

                          {/* set_anchor: on-demand non-persisting preview */}
                          {p.type === 'set_anchor' && (
                            <div className="proposal-preview-block">
                              {!applied && (
                                <button
                                  className="proposal-preview-btn"
                                  onClick={() => previewAnchor(key, p)}
                                  disabled={!documentId || st.preview?.status === 'loading'}
                                  title={!documentId ? 'Add a sample document to preview' : undefined}
                                >
                                  {st.preview?.status === 'loading' ? 'Previewing…' : 'Preview'}
                                </button>
                              )}
                              {st.preview?.status === 'done' && st.preview.reason && (
                                <span className="proposal-preview-reason">{previewReasonText(st.preview.reason)}</span>
                              )}
                              {st.preview?.status === 'done' && !st.preview.reason && (
                                <span className="proposal-preview">
                                  <span className="proposal-preview-before">{String(st.preview.before ?? '—')}</span>
                                  <span className="proposal-preview-arrow">→</span>
                                  <span className="proposal-preview-after">{String(st.preview.after ?? '—')}</span>
                                </span>
                              )}
                              {st.preview?.status === 'error' && (
                                <span className="proposal-apply-error">{st.preview.error}</span>
                              )}
                            </div>
                          )}

                          {/* Apply action */}
                          {meta.applyable ? (
                            <div className="proposal-action">
                              {applied ? (
                                <>
                                  <button className="proposal-apply-btn" disabled>
                                    Applied
                                  </button>
                                  <span className="proposal-diff">{appliedText(p, st)}</span>
                                </>
                              ) : (
                                <button
                                  className="proposal-apply-btn"
                                  onClick={() => applyProposal(key, p)}
                                  disabled={st.apply?.status === 'applying'}
                                >
                                  {st.apply?.status === 'applying' ? 'Applying…' : 'Apply'}
                                </button>
                              )}
                              {st.apply?.status === 'error' && (
                                <span className={st.apply.soft ? 'proposal-apply-note' : 'proposal-apply-error'}>
                                  {st.apply.error}
                                </span>
                              )}
                            </div>
                          ) : (
                            <div className="proposal-info-note">Suggestion only — apply this manually in the editor.</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ),
          )}

          {sending && (
            <div className="assistant-loading">
              <div className="assistant-spinner" />
              <span>Thinking…</span>
            </div>
          )}
        </div>
      )}

      {sendError && (
        <div className="assistant-error">
          {sendError}
          <button className="assistant-retry" onClick={() => send(REVIEW_MESSAGE)} disabled={sending}>
            Try again
          </button>
        </div>
      )}

      {/* Composer */}
      <div className="assistant-composer">
        <textarea
          className="assistant-input"
          placeholder='e.g. "payment_terms is capturing the wrong line"'
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onInputKeyDown}
          rows={2}
          disabled={sending}
        />
        <div className="assistant-composer-actions">
          <button
            className="assistant-quick-btn"
            onClick={() => send(REVIEW_MESSAGE)}
            disabled={sending}
            title="Seed the conversation with a test-run review"
          >
            Review this test run
          </button>
          <button
            className="assistant-review-btn"
            onClick={() => send()}
            disabled={sending || !input.trim()}
          >
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateStudioAssistantPanel;
